<template>
  <div class="font-sans text-gray-800 min-h-screen w-full overflow-x-hidden">
    <!-- Navigation -->
    <nav
      class="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
      aria-label="Navigation principale"
    >
      <div
        class="container mx-auto px-4 py-4 flex justify-between items-center"
      >
        <div class="flex items-center">
          <div class="text-2xl font-bold" :style="{ color: primaryColor }">
            ZARAFA CONSULTING
          </div>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex space-x-8">
          <button
            v-for="item in navItems"
            :key="item.id"
            @click="scrollToSection(item.id)"
            class="font-medium transition-colors duration-300 border-b-2"
            :style="{
              borderColor:
                activeSection === item.id ? primaryColor : 'transparent',
              color: activeSection === item.id ? primaryColor : undefined,
            }"
            :aria-current="activeSection === item.id ? 'page' : undefined"
          >
            {{ item.label }}
          </button>
        </div>

        <!-- Mobile Menu Button -->
        <div class="md:hidden">
          <button
            @click="toggleMenu"
            class="p-2 focus:outline-none"
            :aria-expanded="isMenuOpen"
            aria-controls="mobile-nav"
          >
            <span v-if="isMenuOpen" aria-hidden>✕</span>
            <span v-else aria-hidden>☰</span>
            <span class="sr-only">Ouvrir/fermer le menu</span>
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <transition name="fade-slide">
        <div
          v-if="isMenuOpen"
          id="mobile-nav"
          class="md:hidden bg-white shadow-lg"
        >
          <div class="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <button
              v-for="item in navItems"
              :key="item.id"
              @click="scrollToSection(item.id)"
              class="p-2 font-medium transition-colors duration-300 rounded"
              :style="{
                backgroundColor:
                  activeSection === item.id ? '#f3f4f6' : undefined,
                color: activeSection === item.id ? primaryColor : undefined,
              }"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
      </transition>
    </nav>

    <!-- Hero Section -->
    <section
      id="home"
      ref="homeRef"
      class="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-gray-50 to-gray-100"
    >
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-8 md:mb-0" v-reveal.once="'up'">
            <h1 class="text-4xl md:text-5xl font-bold mb-4">
              Des applications web
              <span :style="{ color: primaryColor }">conçues</span>, développées
              et <span :style="{ color: accentColor }">mises en production</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8">
              ZARAFA CONSULTING développe des applications métier sur mesure, de
              la première maquette à l'exploitation quotidienne. Dernier projet
              en date : Ippo, une plateforme de coaching sportif.
            </p>
            <button
              class="px-6 py-3 rounded-lg text-white font-medium shadow-lg"
              :style="{ backgroundColor: primaryColor }"
              @click="scrollToSection('contact')"
            >
              {{ ctaText }}
            </button>
          </div>
          <div class="md:w-1/2" v-reveal.once>
            <img
              :src="illustrationHero"
              alt=""
              aria-hidden="true"
              class="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" ref="servicesRef" class="py-16 md:py-24 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16" v-reveal.once="'up'">
          <h2 class="text-3xl md:text-4xl font-bold mb-4">
            Domaines d'intervention
          </h2>
          <div
            class="w-20 h-1 mx-auto"
            :style="{ backgroundColor: primaryColor }"
          ></div>
          <p class="text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
            Du développement d'une application complète à l'accompagnement
            ponctuel sur un projet existant.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div
            v-for="(service, index) in services"
            :key="service.id"
            class="bg-gray-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            v-reveal.once="'up'"
            :style="{ animationDelay: `${index * 0.06}s` }"
          >
            <div class="flex items-start">
              <div
                class="p-3 rounded-full mr-4"
                :style="{ backgroundColor: `${service.color}20` }"
              >
                <component
                  :is="service.icon"
                  class="w-6 h-6"
                  :style="{ color: service.color }"
                  aria-hidden="true"
                />
              </div>
              <div>
                <h3 class="text-xl font-bold mb-2">{{ service.title }}</h3>
                <p class="text-gray-600">{{ service.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Réalisations Section -->
    <section
      id="realisations"
      ref="realisationsRef"
      class="py-16 md:py-24 bg-gray-50"
    >
      <div class="container mx-auto px-4">
        <div class="text-center mb-12" v-reveal.once="'up'">
          <h2 class="text-3xl md:text-4xl font-bold mb-4">Réalisations</h2>
          <div
            class="w-20 h-1 mx-auto"
            :style="{ backgroundColor: accentColor }"
          ></div>
          <p class="text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
            Un produit complet, construit de bout en bout.
          </p>
        </div>

        <div
          class="max-w-5xl mx-auto bg-white rounded-lg shadow-md overflow-hidden"
          v-reveal.once="'up'"
        >
          <div class="flex flex-col md:flex-row items-center">
            <div class="md:w-1/3 p-8 flex justify-center">
              <img
                :src="illustrationRealisation"
                alt=""
                aria-hidden="true"
                class="w-40 h-auto md:w-full max-w-[220px]"
              />
            </div>
            <div class="md:w-2/3 p-8 md:pl-4">
              <span
                class="inline-block text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mb-4"
                :style="{
                  backgroundColor: `${accentColor}1A`,
                  color: accentColor,
                }"
              >
                Lancement septembre 2026
              </span>
              <h3 class="text-2xl font-bold mb-3">
                Ippo — plateforme de coaching sportif
              </h3>
              <p class="text-gray-600 mb-4">
                Ippo permet à un coach sportif de construire les programmes
                d'entraînement et les plans nutritionnels de ses athlètes, de
                suivre leurs séances et de gérer ses abonnements. La plateforme
                embarque une bibliothèque de plus de 200 exercices en vidéo.
              </p>
              <p class="text-gray-600 mb-6">
                Conçue et développée intégralement par
                {{ company.legalName }} : modèle de données, interfaces coach et
                athlète, paiement en ligne, hébergement, sauvegardes et
                conformité RGPD.
              </p>
              <p class="text-sm text-gray-500 mb-6">
                Ruby on Rails · Vue 3 · PostgreSQL · Stripe
              </p>
              <a
                href="https://ip-po.fr"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-block px-5 py-2 rounded-lg text-white font-medium shadow"
                :style="{ backgroundColor: primaryColor }"
              >
                Découvrir Ippo
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" ref="aboutRef" class="py-16 md:py-24 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-8 md:mb-0" v-reveal.once>
            <img
              :src="illustrationAbout"
              alt=""
              aria-hidden="true"
              class="w-full h-auto"
            />
          </div>
          <div class="md:w-1/2 md:pl-12" v-reveal.once="'up'">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">À propos</h2>
            <div
              class="w-20 h-1 mb-6"
              :style="{ backgroundColor: accentColor }"
            ></div>
            <p class="text-lg text-gray-600 mb-4">
              {{ company.legalName }} est une société de développement
              informatique basée à Toulon. Derrière, une personne :
              {{ company.publicationDirector }}, développeur, qui prend les
              projets du cadrage jusqu'à la mise en production.
            </p>
            <p class="text-lg text-gray-600 mb-6">
              Pas d'intermédiaire ni de couche de gestion : vous échangez
              directement avec celui qui écrit le code, et qui reste responsable
              du produit une fois qu'il est en ligne.
            </p>
            <div class="space-y-4">
              <div class="flex items-center" v-for="(t, i) in bullets" :key="i">
                <span class="mr-2" :style="{ color: primaryColor }">➤</span>
                <span class="text-gray-700">{{ t }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" ref="contactRef" class="py-16 md:py-24 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12" v-reveal.once>
          <h2 class="text-3xl md:text-4xl font-bold mb-4">Contactez-Nous</h2>
          <div
            class="w-20 h-1 mx-auto"
            :style="{ backgroundColor: primaryColor }"
          ></div>
          <p class="text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
            Un projet à lancer, un code existant à reprendre, ou simplement une
            question technique ? Écrivez, la réponse arrive vite.
          </p>
        </div>

        <div class="max-w-3xl mx-auto">
          <form
            ref="formRef"
            class="bg-gray-50 p-8 rounded-lg shadow-md"
            @submit.prevent="handleSubmit"
            v-reveal.once="'up'"
            :aria-busy="formStatus === 'loading'"
          >
            <!-- Honeypot -->
            <input
              type="text"
              name="_gotcha"
              tabindex="-1"
              autocomplete="off"
              class="hidden"
              aria-hidden
            />

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label for="name" class="block text-gray-700 mb-2">Nom</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  :style="ringStyle"
                  placeholder="Votre nom"
                  required
                />
              </div>
              <div>
                <label for="email" class="block text-gray-700 mb-2"
                  >Email</label
                >
                <input
                  type="email"
                  id="email"
                  name="email"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                  :style="ringStyle"
                  placeholder="Votre email"
                  required
                />
              </div>
            </div>
            <div class="mb-6">
              <label for="subject" class="block text-gray-700 mb-2"
                >Sujet</label
              >
              <input
                type="text"
                id="subject"
                name="subject"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                :style="ringStyle"
                placeholder="Sujet de votre message"
              />
            </div>
            <div class="mb-6">
              <label for="message" class="block text-gray-700 mb-2"
                >Message</label
              >
              <textarea
                id="message"
                name="message"
                rows="5"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                :style="ringStyle"
                placeholder="Votre message"
                required
              ></textarea>
            </div>
            <button
              class="w-full px-6 py-3 rounded-lg text-white font-medium shadow-lg disabled:opacity-60"
              :style="{ backgroundColor: primaryColor }"
              type="submit"
              :disabled="formStatus === 'loading'"
              :aria-disabled="formStatus === 'loading'"
            >
              {{ formStatus === "loading" ? "Envoi…" : "Envoyer" }}
            </button>

            <!-- Mention d'information au point de collecte (RGPD art. 13) -->
            <p class="mt-4 text-xs leading-relaxed text-gray-500">
              Les informations saisies sont utilisées uniquement pour répondre à
              votre demande. Elles sont acheminées par Formspree (États-Unis) et
              conservées 3 ans à compter du dernier échange. Vous disposez d'un
              droit d'accès, de rectification et d'effacement :
              <router-link
                to="/confidentialite"
                class="underline hover:text-gray-700"
                >en savoir plus</router-link
              >.
            </p>

            <!-- ARIA live region for status messages -->
            <div role="status" aria-live="polite" class="mt-4 text-center">
              <p v-if="formStatus === 'success'" class="text-green-600">
                {{ formMessage || "Votre message a été envoyé avec succès." }}
              </p>
              <p v-if="formStatus === 'error'" class="text-red-600">
                {{
                  formMessage || "Une erreur est survenue, veuillez réessayer."
                }}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>

    <SiteFooter :nav-items="navItems" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import {
  CodeBracketIcon,
  Squares2X2Icon,
  RocketLaunchIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/vue/24/outline";
import SiteFooter from "./SiteFooter.vue";
import { company } from "../legal";
import illustrationHero from "../assets/undraw_building_websites_i78t.svg";
import illustrationRealisation from "../assets/undraw_rocket.svg";
import illustrationAbout from "../assets/working.svg";

/* Props (runtime, pas de TS) */

const vReveal = {
  mounted(el, binding) {
    const mode =
      binding?.value === "up" ? "animate-fade-in-up" : "animate-fade-in";
    const once = !binding?.modifiers || binding.modifiers.once !== false;
    el.classList.add("reveal-pending");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add(mode);
            el.classList.remove("reveal-pending");
            if (once) io.unobserve(el);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.2 }
    );
    el.__revealIO = io;
    io.observe(el);
  },
  unmounted(el) {
    el.__revealIO && el.__revealIO.disconnect();
  },
};

const props = defineProps({
  primaryColor: { type: String, default: "#3b82f6" },
  accentColor: { type: String, default: "#10b981" },
  animationDuration: { type: Number, default: 0.5 },
  ctaText: { type: String, default: "Contactez-nous" },
  formspreeEndpoint: {
    type: String,
    default: "https://formspree.io/f/xqaljnrb",
  },
});

/* State dérivé */
const primaryColor = computed(() => props.primaryColor);
const accentColor = computed(() => props.accentColor);
const ctaText = computed(() => props.ctaText);

/* UI state */
const isMenuOpen = ref(false);
const activeSection = ref("home");
const formStatus = ref("idle"); // 'idle' | 'loading' | 'success' | 'error'
const formMessage = ref("");

/* Refs sections + form */
const homeRef = ref(null);
const servicesRef = ref(null);
const realisationsRef = ref(null);
const aboutRef = ref(null);
const contactRef = ref(null);
const formRef = ref(null);

/* Données */
const navItems = [
  { id: "home", label: "Accueil" },
  { id: "services", label: "Services" },
  { id: "realisations", label: "Réalisations" },
  { id: "about", label: "À propos" },
  { id: "contact", label: "Contact" },
];

const bullets = [
  "Un interlocuteur unique, du cadrage à la production",
  "Des produits livrés et exploités, pas des maquettes",
  "Sauvegardes, sécurité et RGPD traités dès le départ",
  "Code et documentation qui vous restent",
];

const services = [
  {
    id: 1,
    title: "Applications web sur mesure",
    description:
      "Développement d'applications métier complètes, du modèle de données à l'interface, avec les tests automatisés qui vont avec.",
    icon: CodeBracketIcon,
    color: primaryColor.value,
  },
  {
    id: 2,
    title: "Interfaces et expérience utilisateur",
    description:
      "Des écrans pensés pour un usage quotidien : parcours lisibles, ergonomie mobile et accessibilité prises au sérieux.",
    icon: Squares2X2Icon,
    color: accentColor.value,
  },
  {
    id: 3,
    title: "Mise en production et exploitation",
    description:
      "Déploiement, sauvegardes testées, authentification des e-mails, conformité RGPD : un produit qui tient une fois en ligne.",
    icon: RocketLaunchIcon,
    color: primaryColor.value,
  },
  {
    id: 4,
    title: "Conseil et reprise de projet",
    description:
      "Audit d'un existant, choix d'architecture, reprise d'un code laissé en plan : un regard extérieur sur vos décisions techniques.",
    icon: ChatBubbleLeftRightIcon,
    color: accentColor.value,
  },
];

/* Focus ring dynamique */
const ringStyle = computed(() => ({ "--tw-ring-color": primaryColor.value }));

/* Actions nav */
function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
}
function scrollToSection(id) {
  activeSection.value = id;
  const map = {
    home: homeRef.value,
    services: servicesRef.value,
    realisations: realisationsRef.value,
    about: aboutRef.value,
    contact: contactRef.value,
  };
  const section = map[id];
  if (section) section.scrollIntoView({ behavior: "smooth" });
  isMenuOpen.value = false;
}

/* Observer pour section active */
let observer = null;
onMounted(() => {
  const targets = [
    homeRef.value,
    servicesRef.value,
    realisationsRef.value,
    aboutRef.value,
    contactRef.value,
  ].filter(Boolean);
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("id");
          if (id) activeSection.value = id;
        }
      });
    },
    { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.25 }
  );
  targets.forEach((el) => observer.observe(el));
});
onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

/* Form submit (AJAX) */
async function handleSubmit() {
  if (formStatus.value === "loading") return;
  formStatus.value = "loading";
  formMessage.value = "";

  const form = formRef.value;
  if (!form) return;
  const formData = new FormData(form);

  // Honeypot
  const botValue = String(formData.get("_gotcha") || "").trim();
  if (botValue) {
    formStatus.value = "success";
    formMessage.value = "Merci !";
    form.reset();
    return;
  }

  // Conseils Formspree
  const replyTo = String(formData.get("email") || "");
  if (replyTo) formData.set("_replyto", replyTo);
  if (!formData.get("_subject"))
    formData.set("_subject", "Nouveau message — Zarafa Consulting");
  formData.set("_template", "table");

  try {
    const res = await fetch(props.formspreeEndpoint, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    });
    if (res.ok) {
      formStatus.value = "success";
      formMessage.value = "Votre message a été envoyé avec succès.";
      form.reset();
    } else {
      const data = await res.json().catch(() => ({}));
      const msg =
        data?.errors?.[0]?.message ||
        "Une erreur est survenue, veuillez réessayer.";
      formStatus.value = "error";
      formMessage.value = msg;
    }
  } catch {
    formStatus.value = "error";
    formMessage.value =
      "Réseau indisponible. Merci de réessayer dans un instant.";
  }
}
</script>

<style scoped>
/* Simple transitions (no external libs) */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  height: 0;
}

.reveal-pending {
  opacity: 0;
  transform: translateY(12px);
}

.animate-fade-in {
  animation: fadeIn var(--anim, 0.5s) ease both;
}
.animate-fade-in-up {
  animation: fadeInUp var(--anim, 0.5s) ease both;
}
.animate-fade-in-left {
  animation: fadeInLeft var(--anim, 0.5s) ease both;
}
.animate-fade-in-right {
  animation: fadeInRight var(--anim, 0.5s) ease both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(-12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
</style>
